import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { notifyOrgManagers } from "@/lib/notifications";

export type MaintenanceStatus = "open" | "in_progress" | "on_hold" | "completed" | "cancelled";
export type MaintenancePriority = "urgent" | "high" | "medium" | "low";
export type MaintenanceCategory =
  | "plumbing" | "electrical" | "hvac" | "elevator" | "exterior"
  | "common_area" | "unit" | "general" | "other";

export interface MaintenanceRequest {
  id: string;
  org_id: string;
  unit_id: string | null;
  submitted_by: string | null;
  assigned_to: string | null;
  title: string;
  description: string;
  category: MaintenanceCategory;
  priority: MaintenancePriority;
  status: MaintenanceStatus;
  photo_urls: string[];
  resolution_note: string | null;
  estimated_cost: number | null;
  actual_cost: number | null;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // joined
  submitter?: { full_name: string | null; email: string | null } | null;
  assignee?: { full_name: string | null; email: string | null } | null;
  unit?: { unit_number: string } | null;
}

export function useMaintenanceRequests(statusFilter?: MaintenanceStatus | "all") {
  const { activeOrgId } = useAuth();
  return useQuery({
    queryKey: ["maintenance", activeOrgId, statusFilter],
    enabled: !!activeOrgId,
    queryFn: async () => {
      let q = supabase
        .from("maintenance_requests")
        .select(`
          *,
          submitter:profiles!maintenance_requests_submitted_by_fkey(full_name, email),
          assignee:profiles!maintenance_requests_assigned_to_fkey(full_name, email),
          unit:units!maintenance_requests_unit_id_fkey(unit_number)
        `)
        .eq("org_id", activeOrgId!)
        .order("created_at", { ascending: false });
      if (statusFilter && statusFilter !== "all") q = q.eq("status", statusFilter);
      const { data, error } = await q;
      if (error) throw error;
      return data as MaintenanceRequest[];
    },
  });
}

export function useCreateMaintenanceRequest() {
  const queryClient = useQueryClient();
  const { user, activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (req: {
      title: string;
      description: string;
      category?: MaintenanceCategory;
      priority?: MaintenancePriority;
      unit_id?: string;
      due_date?: string;
      estimated_cost?: number;
    }) => {
      if (!activeOrgId) throw new Error("No active organization");
      const { data, error } = await supabase
        .from("maintenance_requests")
        .insert({ ...req, org_id: activeOrgId, submitted_by: user?.id })
        .select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance", activeOrgId] });
      // Notify all org managers (fire-and-forget)
      if (activeOrgId) {
        notifyOrgManagers(activeOrgId, {
          type: "info",
          title: "New Maintenance Request",
          message: `"${data?.title ?? "A new request"}" has been submitted and needs attention.`,
          data: { link: "/maintenance" },
        }).catch(() => {/* silent */});
      }
      toast({ title: "Request submitted", description: "Your maintenance request has been logged." });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useUpdateMaintenanceRequest() {
  const queryClient = useQueryClient();
  const { activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id, ...updates
    }: Partial<MaintenanceRequest> & { id: string }) => {
      if (updates.status === "completed") {
        (updates as any).completed_at = new Date().toISOString();
      }
      const { data, error } = await supabase
        .from("maintenance_requests")
        .update(updates).eq("id", id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance", activeOrgId] });
      const msg = vars.status === "completed" ? "Marked as completed"
        : vars.status ? `Status: ${vars.status}` : "Request updated";
      toast({ title: "Updated", description: msg });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export const PRIORITY_CONFIG: Record<MaintenancePriority, { label: string; color: string; bg: string }> = {
  urgent:  { label: "Urgent",  color: "text-red-700",    bg: "bg-red-100 border-red-300" },
  high:    { label: "High",    color: "text-orange-700", bg: "bg-orange-100 border-orange-300" },
  medium:  { label: "Medium",  color: "text-amber-700",  bg: "bg-amber-100 border-amber-300" },
  low:     { label: "Low",     color: "text-blue-700",   bg: "bg-blue-100 border-blue-300" },
};

export const STATUS_CONFIG: Record<MaintenanceStatus, { label: string; color: string }> = {
  open:        { label: "Open",        color: "text-amber-600" },
  in_progress: { label: "In Progress", color: "text-blue-600" },
  on_hold:     { label: "On Hold",     color: "text-gray-500" },
  completed:   { label: "Completed",   color: "text-green-600" },
  cancelled:   { label: "Cancelled",   color: "text-muted-foreground" },
};

export function useDeleteMaintenanceRequest() {
  const queryClient = useQueryClient();
  const { activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("maintenance_requests")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance", activeOrgId] });
      toast({ title: "Request deleted", description: "The maintenance request has been removed." });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}
